import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, addDoc, collection } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  OutlinedInput,
  Grid,
  Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReactMarkdown from 'react-markdown';

const ANNOUNCEMENT_TYPES = ['standard', 'version'];
const PLATFORMS = ['ios', 'android'];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function AnnouncementDetail() {
  const { announcementId } = useParams();
  const navigate = useNavigate();
  const isNewAnnouncement = announcementId === 'new';
  
  const [loading, setLoading] = useState(!isNewAnnouncement);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [announcement, setAnnouncement] = useState({
    type: 'standard',
    version: '',
    expirationDate: '',
    published: false,
    repeatViewFrequency: 0,
    repeatViews: 0,
    platform: [],
    body: '',
    title: ''
  });

  useEffect(() => {
    const fetchAnnouncement = async () => {
      if (isNewAnnouncement) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const announcementDoc = await getDoc(doc(db, 'announcements', announcementId));
        
        if (announcementDoc.exists()) {
          const data = announcementDoc.data();
          setAnnouncement({
            type: data.type || 'standard',
            version: data.version || '',
            expirationDate: data.expirationDate || '',
            published: data.published || false,
            repeatViewFrequency: data.repeatViewFrequency || 0,
            repeatViews: data.repeatViews || 0,
            platform: data.platform || [],
            body: data.body || '',
            title: data.title || ''
          });
        } else {
          setError('Announcement not found');
        }
      } catch (err) {
        console.error('Error fetching announcement:', err);
        setError('Failed to load announcement');
      } finally {
        setLoading(false);
      }
    };

    fetchAnnouncement();
  }, [announcementId, isNewAnnouncement]);

  const handleChange = (field, value) => {
    setAnnouncement(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
    setSuccess(false);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validation
      if (!announcement.title.trim()) {
        setError('Title is required');
        setLoading(false);
        return;
      }

      if (!announcement.body.trim()) {
        setError('Body is required');
        setLoading(false);
        return;
      }

      const announcementData = {
        type: announcement.type,
        version: announcement.version,
        expirationDate: announcement.expirationDate,
        published: announcement.published,
        repeatViewFrequency: parseInt(announcement.repeatViewFrequency) || 0,
        repeatViews: parseInt(announcement.repeatViews) || 0,
        platform: announcement.platform,
        body: announcement.body,
        title: announcement.title
      };

      if (isNewAnnouncement) {
        const docRef = await addDoc(collection(db, 'announcements'), announcementData);
        setSuccess(true);
        setTimeout(() => {
          navigate(`/announcements/${docRef.id}`);
        }, 1000);
      } else {
        await updateDoc(doc(db, 'announcements', announcementId), announcementData);
        setSuccess(true);
      }
    } catch (err) {
      console.error('Error saving announcement:', err);
      setError('Failed to save announcement');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !isNewAnnouncement) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/announcements')}
        sx={{ mb: 2 }}
      >
        Back to Announcements
      </Button>

      <Typography variant="h4" gutterBottom>
        {isNewAnnouncement ? 'New Announcement' : 'Edit Announcement'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Announcement saved successfully!
        </Alert>
      )}

      <Paper sx={{ p: 3, mt: 2, maxWidth: 900, mx: 'auto' }}>
        <Box sx={{ mb: 3 }}>
          <TextField
            label="Title"
            value={announcement.title}
            onChange={(e) => handleChange('title', e.target.value)}
            required
            sx={{ mb: 3, width: 600 }}
          />
        </Box>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              label="Type"
              value={announcement.type}
              onChange={(e) => handleChange('type', e.target.value)}
              sx={{ width: 250 }}
            >
              {ANNOUNCEMENT_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Version"
              value={announcement.version}
              onChange={(e) => handleChange('version', e.target.value)}
              helperText="Leave blank for all versions"
              sx={{ width: 250 }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <FormControl sx={{ width: 250 }}>
              <InputLabel>Platform</InputLabel>
              <Select
                multiple
                value={announcement.platform}
                onChange={(e) => handleChange('platform', e.target.value)}
                input={<OutlinedInput label="Platform" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} />
                    ))}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {PLATFORMS.map((platform) => (
                  <MenuItem key={platform} value={platform}>
                    {platform.charAt(0).toUpperCase() + platform.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              type="number"
              label="Repeat Every X Days"
              value={announcement.repeatViews}
              onChange={(e) => handleChange('repeatViews', e.target.value)}
              inputProps={{ min: 0 }}
              sx={{ width: 250 }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              type="number"
              label="Until Viewed X Times"
              value={announcement.repeatViewFrequency}
              onChange={(e) => handleChange('repeatViewFrequency', e.target.value)}
              inputProps={{ min: 0 }}
              helperText="Days between repeat views"
              sx={{ width: 250 }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              type="date"
              label="Expiration Date"
              value={announcement.expirationDate}
              onChange={(e) => handleChange('expirationDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 250 }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={announcement.published}
                  onChange={(e) => handleChange('published', e.target.checked)}
                />
              }
              label="Published"
            />
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ mb: 3 }}>
          <TextField
            label="Body (Markdown)"
            value={announcement.body}
            onChange={(e) => handleChange('body', e.target.value)}
            multiline
            rows={12}
            required
            helperText="Use Markdown formatting - preview below"
            sx={{
              width: '100%',
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
              }
            }}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Preview
          </Typography>
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2, 
              minHeight: 200,
              backgroundColor: '#f5f5f5',
              overflow: 'auto',
              wordWrap: 'break-word',
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                marginTop: 2,
                marginBottom: 1,
                wordWrap: 'break-word'
              },
              '& p': {
                marginBottom: 1,
                wordWrap: 'break-word'
              },
              '& ul, & ol': {
                marginLeft: 2
              },
              '& code': {
                backgroundColor: '#e0e0e0',
                padding: '2px 4px',
                borderRadius: 1,
                wordWrap: 'break-word',
                overflowWrap: 'break-word'
              },
              '& pre': {
                backgroundColor: '#e0e0e0',
                padding: 2,
                borderRadius: 1,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word'
              },
              '& img': {
                maxWidth: '100%',
                height: 'auto'
              },
              '& a': {
                wordWrap: 'break-word',
                overflowWrap: 'break-word'
              }
            }}
          >
            {announcement.body ? (
              <ReactMarkdown>{announcement.body}</ReactMarkdown>
            ) : (
              <Typography color="text.secondary" fontStyle="italic">
                Preview will appear here...
              </Typography>
            )}
          </Paper>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/announcements')}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

export default AnnouncementDetail;
